import { createContext, useCallback, useContext, useEffect, useLayoutEffect, useRef, useState } from 'react';
import {
  heroContentScale,
  nextPinchScale,
  pinchDistance,
  readContentScale,
  writeContentScale,
} from '../../lib/contentScale';

const ScaleContext = createContext({ scale: 1, heroScale: 1 });
const PlainStateContext = createContext(false);
const PlainClaimContext = createContext(null);

export function useContentScale() {
  return useContext(ScaleContext);
}

export function useChromePlain() {
  return useContext(PlainStateContext);
}

// Pages with sticky bars call this so the shell does not scale their
// whole tree. A transform on an ancestor would keep those bars from
// sticking under the header. The page then scales only its body.
export function usePlainChrome() {
  const claim = useContext(PlainClaimContext);
  const id = useRef(null);
  if (id.current == null) id.current = Symbol('plain-chrome');
  useLayoutEffect(() => {
    if (!claim) return undefined;
    claim(id.current, true);
    return () => claim(id.current, false);
  }, [claim]);
}

export function ContentScaleProvider({ children }) {
  const [scale, setScale] = useState(() => readContentScale());
  const [plain, setPlain] = useState(false);
  const scaleRef = useRef(scale);
  const holders = useRef(new Set());
  scaleRef.current = scale;

  const claim = useCallback((id, on) => {
    if (on) holders.current.add(id);
    else holders.current.delete(id);
    setPlain(holders.current.size > 0);
  }, []);

  useEffect(() => {
    const gesture = { active: false, startDist: 0, base: 1 };

    const onStart = (event) => {
      if (event.touches?.length !== 2) {
        gesture.active = false;
        return;
      }
      gesture.active = true;
      gesture.startDist = pinchDistance(event.touches[0], event.touches[1]);
      gesture.base = scaleRef.current;
    };

    const onMove = (event) => {
      if (!gesture.active || event.touches?.length !== 2) return;
      if (event.cancelable) event.preventDefault();
      const next = nextPinchScale(
        gesture.base,
        gesture.startDist,
        pinchDistance(event.touches[0], event.touches[1]),
      );
      if (next === scaleRef.current) return;
      scaleRef.current = next;
      setScale(next);
    };

    const onEnd = (event) => {
      if (event.touches?.length >= 2) return;
      if (!gesture.active) return;
      gesture.active = false;
      const saved = writeContentScale(scaleRef.current);
      scaleRef.current = saved;
      setScale(saved);
    };

    // WebKit pinch also fires gesture*. Cancelling it keeps Safari from
    // magnifying the viewport. The touch handler scales the page instead.
    const stopNative = (event) => {
      event.preventDefault();
    };

    document.addEventListener('touchstart', onStart, { passive: true });
    document.addEventListener('touchmove', onMove, { passive: false });
    document.addEventListener('touchend', onEnd);
    document.addEventListener('touchcancel', onEnd);
    document.addEventListener('gesturestart', stopNative, { passive: false });
    document.addEventListener('gesturechange', stopNative, { passive: false });
    return () => {
      document.removeEventListener('touchstart', onStart);
      document.removeEventListener('touchmove', onMove);
      document.removeEventListener('touchend', onEnd);
      document.removeEventListener('touchcancel', onEnd);
      document.removeEventListener('gesturestart', stopNative);
      document.removeEventListener('gesturechange', stopNative);
    };
  }, []);

  const value = { scale, heroScale: heroContentScale(scale) };
  return (
    <PlainClaimContext.Provider value={claim}>
      <PlainStateContext.Provider value={plain}>
        <ScaleContext.Provider value={value}>
          {children}
        </ScaleContext.Provider>
      </PlainStateContext.Provider>
    </PlainClaimContext.Provider>
  );
}

export function ScaledBlock({ children, className = '', active = true }) {
  const { scale } = useContentScale();
  const slotRef = useRef(null);
  const innerRef = useRef(null);
  const scaling = active && scale !== 1;

  useLayoutEffect(() => {
    const slot = slotRef.current;
    const inner = innerRef.current;
    if (!slot || !inner) return undefined;
    if (!scaling) {
      slot.style.height = '';
      return undefined;
    }
    const apply = () => {
      slot.style.height = `${inner.offsetHeight * scale}px`;
    };
    apply();
    if (typeof ResizeObserver === 'undefined') return undefined;
    const observer = new ResizeObserver(apply);
    observer.observe(inner);
    return () => observer.disconnect();
  }, [scaling, scale, children]);

  return (
    <div
      ref={slotRef}
      className={`pik-scale-slot w-full ${className}`.trim()}
      {...(active ? { 'data-content-scale': scale } : {})}
      style={scaling ? { overflow: 'hidden' } : undefined}
    >
      <div
        ref={innerRef}
        className="pik-scale-inner"
        style={active ? {
          transform: `scale(${scale})`,
          transformOrigin: 'top left',
          width: scale === 1 ? '100%' : `${100 / scale}%`,
        } : undefined}
      >
        {children}
      </div>
    </div>
  );
}

// The wrapper stays mounted when a page claims plain chrome. Swapping it
// out for the bare children would remount the page and drop contact edit
// state. Inactive slots have no transform, so sticky bars still stick to
// the main scroller.
export function ShellBody({ children }) {
  const plain = useChromePlain();
  return <ScaledBlock active={!plain}>{children}</ScaledBlock>;
}
